import React from "react";
import styled from "styled-components";
import { ITheme } from "../Themes/ThemeTypes";
import Icon from "../Icon/Icon";
import { ThemeColorEnum, ThemeIconEnum, ThemeIconSizeEnum } from "../Themes/ThemeEnums";
import { PicoAppLogoProps } from "./picoAppLogoMap";

export type AppCardVariant = "card" | "tile";

const Card = styled.a<{ theme: ITheme }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 16px;
  background: rgb(${(props) => props.theme.colors.surface_container});
  color: rgb(${(props) => props.theme.colors.on_surface});
  font-family: ${(props) => props.theme.base.typography.font};
  text-decoration: none;
  cursor: pointer;
  min-width: 240px;
  max-width: 320px;
  transition:
    box-shadow ${(props) => props.theme.base.motion.duration.short4}ms
      ${(props) => props.theme.base.motion.easing.standard},
    background-color ${(props) => props.theme.base.motion.duration.short4}ms
      ${(props) => props.theme.base.motion.easing.standard};

  &:hover {
    box-shadow: ${(props) => props.theme.base.elevation.level1_shadow};
    background: rgb(${(props) => props.theme.colors.surface_container_high});
  }
`;

const Title = styled.div<{ theme: ITheme }>`
  font-size: ${(props) => props.theme.base.typography.title.large.font_size}px;
  font-weight: ${(props) => props.theme.base.typography.title.large.font_weight};
  line-height: ${(props) => props.theme.base.typography.title.large.line_height}px;
`;

const Description = styled.div<{ theme: ITheme }>`
  font-size: ${(props) => props.theme.base.typography.body.medium.font_size}px;
  line-height: ${(props) => props.theme.base.typography.body.medium.line_height}px;
  color: rgb(${(props) => props.theme.colors.on_surface_variant});
`;

const Tile = styled.a<{ theme: ITheme }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 8px;
  aspect-ratio: 1;
  border-radius: 16px;
  background: transparent;
  color: rgb(${(props) => props.theme.colors.on_surface});
  font-family: ${(props) => props.theme.base.typography.font};
  text-decoration: none;
  cursor: pointer;
  text-align: center;
  transition: background-color ${(props) => props.theme.base.motion.duration.short4}ms
    ${(props) => props.theme.base.motion.easing.standard};

  &:hover {
    background: rgb(${(props) => props.theme.colors.surface_container_high});
  }
`;

const TileName = styled.div<{ theme: ITheme }>`
  font-size: ${(props) => props.theme.base.typography.title.small.font_size}px;
  font-weight: ${(props) => props.theme.base.typography.title.small.font_weight};
  line-height: ${(props) => props.theme.base.typography.title.small.line_height}px;
`;

interface AppCardProps {
  name: string;
  description?: string;
  href: string;
  icon?: ThemeIconEnum;
  logo?: React.FC<PicoAppLogoProps>;
  variant?: AppCardVariant;
}

const AppCard: React.FC<AppCardProps> = ({
  name,
  description,
  href,
  icon,
  logo: Logo,
  variant = "card",
}) => {
  if (variant === "tile") {
    return (
      <Tile href={href}>
        {Logo ? (
          <Logo size={ThemeIconSizeEnum.EXTRA_LARGE} />
        ) : (
          icon !== undefined && (
            <Icon icon={icon} color={ThemeColorEnum.PRIMARY} size={ThemeIconSizeEnum.EXTRA_LARGE} />
          )
        )}
        <TileName>{name}</TileName>
      </Tile>
    );
  }

  return (
    <Card href={href}>
      {Logo ? <Logo /> : icon !== undefined && <Icon icon={icon} color={ThemeColorEnum.PRIMARY} />}
      <Title>{name}</Title>
      {description && <Description>{description}</Description>}
    </Card>
  );
};

export default AppCard;
